import React, { useState } from 'react';
import { Container, Typography, Box, Grid, Button, Card, CardContent, Paper, Divider, Icon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import PageHeader from '../components/PageHeader';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  Bolt, Groups, Psychology, WorkspacePremium, Diversity3, EmojiObjects, Handshake, ContactMail,
  Adjust, VerifiedUser, Tune, Balance, RecordVoiceOver, CheckCircleOutline,
  Hearing, Visibility, PlaylistAddCheck, Security, TrendingUp, Flag, Lightbulb
} from '@mui/icons-material'; // Import selected icons

// Helper function to get the correct icon based on ID
const getIcon = (id: string) => {
  switch (id) {
    case 'energy': return <Bolt color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>;
    case 'focused': return <Groups color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Using Groups
    case 'interactive': return <Psychology color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>;
    case 'safe': return <VerifiedUser color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Using VerifiedUser
    case 'flexible': return <Tune color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Using Tune
    case 'balance': return <Balance color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Using Balance
    case 'attention': return <RecordVoiceOver color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Using RecordVoiceOver
    default: return <CheckCircleOutline color="primary" sx={{ fontSize: 48, mb: 2, mx: 'auto' }}/>; // Fallback
  }
};

// Helper function to get icon for Why Me Coach Expect Items
const getWhyMeCoachIcon = (id: string) => {
  const iconStyle = { fontSize: 48, mb: 2, mx: 'auto' };
  switch (id) {
    case 'listen': return <Hearing color="secondary" sx={iconStyle} />;
    case 'insight': return <Visibility color="secondary" sx={iconStyle} />;
    case 'plan': return <PlaylistAddCheck color="secondary" sx={iconStyle} />;
    case 'safe': return <Security color="secondary" sx={iconStyle} />;
    case 'growth': return <TrendingUp color="secondary" sx={iconStyle} />;
    default: return <CheckCircleOutline color="secondary" sx={iconStyle} />; // Fallback
  }
};

const Over = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = translations[language];
  const theme = useTheme();
  const [isVisionExpanded, setIsVisionExpanded] = useState(false);

  const paragraphStyle = {
    color: 'text.secondary',
    mb: 2,
    fontSize: '1.05rem',
    lineHeight: 1.7
  };
  
  // Split approachText into parts based on the embedded title
  const coachApproachParts = t.over.whyMe.coach.approachText.split('\n\nJouw reis, professioneel begeleid\n');
  const coachApproachMain = coachApproachParts[0];
  const coachApproachJourney = coachApproachParts.length > 1 ? coachApproachParts[1] : '';

  // Define styles for truncated text and blur effect
  const visionTextStyle = {
    overflow: 'hidden',
    position: 'relative',
    transition: 'max-height 0.5s ease-in-out',
    maxHeight: isVisionExpanded ? '1000px' : '250px', // Increased initial height for alignment
    '&::after': isVisionExpanded ? {} : {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '40px',
      // Use theme background paper color for the gradient end
      background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
      pointerEvents: 'none',
    },
  };

  return (
    <>
      {/* Replace custom Hero Section with PageHeader */}
      <PageHeader
        title={language === 'nl' ? 'Loopbaancoach Hanneke van Keulen' : t.over.pageTitle}
        subtitle={t.over.pageSubtitle}
      />

      {/* Over Mij Section */}
      <Box sx={{ py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 4, md: 6 }} alignItems="stretch">
            {/* Left Column: Image and Mission/Vision */}
            <Grid item xs={12} md={5} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Image */}
              <Box>
                <Box
                  component="img"
                  src="/Photo1_me.png"
                  alt={t.over.introTitle}
                  sx={{
                      width: '100%',
                      objectFit: 'cover',
                      display: 'block',
                      borderRadius: '12px',
                      boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
                  }}
                />
              </Box>

              {/* Mission Section */}
              <Paper elevation={1} sx={{ p: {xs: 2, sm: 3}, borderRadius: 2, width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                     <Flag color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                     <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 0 }}>
                       {t.over.mission.title}
                     </Typography>
                   </Box>
                   <Typography variant="body1" sx={{...paragraphStyle, pl: '44px', flexGrow: 1 }}>
                      {t.over.mission.description}
                    </Typography>
              </Paper>

              {/* Vision Section - Apply changes here */}
              <Paper 
                elevation={1} 
                sx={{ 
                  p: {xs: 2, sm: 3}, 
                  borderRadius: 2, 
                  width: '100%', 
                  position: 'relative', // Needed for absolute positioning of button
                  display: 'flex', // Added flex
                  flexDirection: 'column' // Added flex
                }}
              >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                     <Lightbulb color="primary" sx={{ mr: 1.5, fontSize: 28 }} />
                     <Typography variant="h6" gutterBottom color="primary.main" sx={{ mb: 0 }}>
                       {t.over.vision.title}
                     </Typography>
                   </Box>
                   {/* Wrap text in Box with styles */}
                   <Box sx={{ ...visionTextStyle, pl: '44px', flexGrow: 1, mb: 6 /* Margin for button */ }}> 
                     <Typography variant="body1" sx={{...paragraphStyle, whiteSpace: 'pre-line' }}> 
                       {t.over.vision.description}
                     </Typography>
                   </Box>
                   {/* Add Read more/less button */}
                   <Button 
                     onClick={() => setIsVisionExpanded(!isVisionExpanded)}
                     variant="text"
                     sx={{ 
                       position: 'absolute',
                       bottom: '16px', // Adjust spacing from bottom
                       left: '24px', // Adjust spacing from left (matches padding p:3)
                       alignSelf: 'flex-start', 
                       mt: 1, // Minimal top margin
                       pl: '44px' // Align text with description text
                     }}
                   >
                     {isVisionExpanded ? t.over.vision.readLess : t.over.vision.readMore}
                   </Button>
              </Paper>
            </Grid>

            {/* Right Column: Intro Text and Button */}
            <Grid item xs={12} md={7}>
              {/* Wrap content in Paper for white box effect */}
              <Paper
                elevation={2}
                sx={{
                  p: {xs: 2, sm: 3, md: 4}, // Responsive padding
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2, // Match other paper components
                  boxShadow: theme.shadows[2] // Subtle shadow
                }}
              >
                <Typography variant="h4" color="primary.main" gutterBottom sx={{ fontWeight: 500, mb: 1 }}>
                  {t.over.introTitle}
                </Typography>
                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontWeight: 400, mb: 3 }}>
                  {t.over.introSubtitle}
                </Typography>
                <Typography variant="body1" paragraph sx={{ ...paragraphStyle, whiteSpace: 'pre-line', color: 'text.primary', flexGrow: 1 }}>
                  {t.over.introText}
                </Typography>

                {/* Waarom Ik Button - Moved Here */}
                <Box sx={{ mt: 3, alignSelf: 'flex-start' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => navigate('/waarom-ik')}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 1.5,
                      px: 3,
                      borderRadius: 2,
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      boxShadow: theme.shadows[3]
                    }}
                  >
                    Waarom voor mij kiezen?
                  </Button>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Keep Divider before next section */}
      <Divider sx={{ my: { xs: 4, md: 5 }, width: '80%', mx: 'auto' }} />

      {/* Wat kun je verwachten Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'primary.lightest' /* Soft background color */ }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 600, mb: 6, color: 'primary.dark' }}>
            Wat kun je van mij verwachten?
          </Typography>
          <Grid container spacing={4} justifyContent="center">
            {[ // Define the items inline for clarity
              { id: 'safe', title: 'Vertrouwelijke omgeving', text: 'Een veilige ruimte waar je vrijuit kunt spreken over je ambities, twijfels en uitdagingen.', icon: <Security color="primary" sx={{ fontSize: 40 }} /> },
              { id: 'targeted', title: 'Gerichte begeleiding', text: 'Persoonlijke coaching die volledig is afgestemd op jouw situatie en doelen.', icon: <Adjust color="primary" sx={{ fontSize: 40 }} /> },
              { id: 'insight', title: 'Inzicht en helderheid', text: 'Ontdek waar je écht gelukkig van wordt en welke carrièrestappen daarbij passen.', icon: <EmojiObjects color="primary" sx={{ fontSize: 40 }} /> },
              { id: 'practical', title: 'Praktische handvatten', text: 'Concrete tools en strategieën om direct toe te passen in je zoektocht naar werkgeluk.', icon: <WorkspacePremium color="primary" sx={{ fontSize: 40 }} /> }
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.id}>
                <Paper 
                  elevation={3} 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center', 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: theme.shadows[6]
                    }
                  }}
                >
                  <Box sx={{ mb: 2 }}>{item.icon}</Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Appointment CTA - Just the button */}
      <Box sx={{ py: { xs: 4, md: 5 }, bgcolor: 'background.paper', textAlign: 'center' }}>
        <Container maxWidth="sm">
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<ContactMail />}
            onClick={() => navigate('/contact', { state: { subject: 'Kennismakingsgesprek Loopbaancoaching' } })}
            sx={{ 
              py: 1.5, 
              px: 5, 
              borderRadius: 2, 
              fontWeight: 'bold',
              fontSize: '1.1rem',
              boxShadow: theme.shadows[4],
              color: 'white'
            }}
          >
            Maak een afspraak
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default Over; 
